import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Box, Button, Fade, Modal } from "@mui/material";
import { useSelector } from "react-redux";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import { API_ENDPOINT } from "../../../services/api/resources";
import SuccessPopupDashboard from "../../../components/Popups/PopupFrame/SuccessPopupDashboard";
import successPopupImg from "../../../assets/images/png/Group 2 2@2x.png";
import SearchBar from "../../../components/common/SearchBar/SearchBar";
import DataTable from "../../../components/common/DataTable/DataTableSorting";
import PaginationComponent from "../../../components/common/Pagination/PaginationComponent";
import styles from "./SupplierDatabase.module.css";

const ACCEPTED_TYPES = ".csv,.xls,.xlsx";

const SupplierDatabase = () => {
  const loginData = useSelector((state) => state.loginData.value);
  const authToken = loginData?.auth_token || loginData?.access_token || loginData?.token;
  const superadminToken = loginData?.token;
  const inputRef = useRef(null);
  const [isUploadView, setIsUploadView] = useState(false);
  const [file, setFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [importSummary, setImportSummary] = useState(null);
  const [mappings, setMappings] = useState([]);
  const [expandedLocations, setExpandedLocations] = useState({});
  const [locationOverflow, setLocationOverflow] = useState({});
  const [saveSuccessOpen, setSaveSuccessOpen] = useState(false);
  const [savedMappings, setSavedMappings] = useState([]);
  const [savedTotal, setSavedTotal] = useState(0);
  const [savedSearch, setSavedSearch] = useState("");
  const [savedPage, setSavedPage] = useState(1);
  const [isSavedTableLoading, setIsSavedTableLoading] = useState(false);
  const locationRefs = useRef({});

  useEffect(() => {
    const updateOverflow = () => {
      const nextOverflow = {};
      Object.entries(locationRefs.current).forEach(([index, node]) => {
        if (node) nextOverflow[index] = node.scrollHeight > node.clientHeight + 1;
      });
      setLocationOverflow(nextOverflow);
    };

    updateOverflow();
    window.addEventListener("resize", updateOverflow);
    return () => window.removeEventListener("resize", updateOverflow);
  }, [mappings]);

  const fetchSavedMappings = async (page = savedPage, search = savedSearch) => {
    if (!superadminToken) return;
    setIsSavedTableLoading(true);
    try {
      const { data } = await axios.post(
        API_ENDPOINT.imported_item_vendors_view,
        { superadmin_token: superadminToken, search: search.trim(), page, page_size: 10 },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      const rows = Array.isArray(data)
        ? data
        : data?.mappings || data?.results || data?.data || data?.response || (data?.item_code ? [data] : []);
      setSavedMappings(rows);
      setSavedTotal(Number(data?.count ?? data?.total_count ?? data?.total ?? rows.length));
    } catch {
      setSavedMappings([]);
      setSavedTotal(0);
    } finally {
      setIsSavedTableLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => fetchSavedMappings(), 250);
    return () => clearTimeout(timer);
  }, [superadminToken, savedPage, savedSearch]);

  const selectFile = (nextFile) => {
    if (!nextFile) return;
    if (!/\.(csv|xls|xlsx)$/i.test(nextFile.name)) {
      setError("Please select a CSV or Excel file (.csv, .xls, .xlsx).");
      return;
    }
    setFile(nextFile);
    setError("");
    setMappings([]);
    setExpandedLocations({});
  };

  const uploadFile = async () => {
    if (!file) {
      setError("Select a file before uploading.");
      return;
    }
    const formData = new FormData();
    formData.append("file", file);
    setIsUploading(true);
    setError("");
    try {
      const { data } = await axios.post(
        API_ENDPOINT.imported_item_vendors_import,
        formData,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      setImportSummary(data || {});
      setMappings(Array.isArray(data?.mappings) ? data.mappings : []);
    } catch (uploadError) {
      setError(uploadError?.response?.data?.message || uploadError?.response?.data?.error || "Unable to import the supplier database. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  const clearUploadData = () => {
    setFile(null);
    setMappings([]);
    setImportSummary(null);
    setExpandedLocations({});
    setLocationOverflow({});
    setIsDragging(false);
    setError("");
    if (inputRef.current) inputRef.current.value = "";
  };

  const saveMappings = async () => {
    if (!mappings.length) return;
    setIsSaving(true);
    setError("");
    try {
      await axios.post(
        API_ENDPOINT.imported_item_vendors_save,
        { mappings, superadmin_token: loginData?.token },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      clearUploadData();
      setSavedPage(1);
      setSaveSuccessOpen(true);
      void fetchSavedMappings(1);
    } catch (saveError) {
      setError(saveError?.response?.data?.message || saveError?.response?.data?.error || "Unable to save the supplier database. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const returnToSupplierDatabase = () => {
    clearUploadData();
    setSaveSuccessOpen(false);
    setIsUploadView(false);
  };

  const savedMappingsHeadings = [
    { label: "Item Code", field: "item_code", col_width: "13rem", minWidth: "13rem" },
    { label: "Item Name", field: "item_name", col_width: "20rem", minWidth: "20rem" },
    { label: "Vendor Name", field: "vendor_name", col_width: "20rem", minWidth: "20rem" },
    { label: "Supply Location", field: "supply_location", col_width: "32rem", minWidth: "32rem" },
    {
      label: "Email",
      field: "email",
      col_width: "24rem",
      minWidth: "24rem",
      renderCell: (row) => <span className={styles.emailCell}>{row.email || "-"}</span>,
    },
  ];

  if (isUploadView) {
    return (
      <section className={styles.page}>
        <div className={styles.header}>
          <div>
            <h1>Upload Supplier Database </h1>
            <p>Import external suppliers and their item offerings from a spreadsheet.</p>
          </div>
          <Button variant="outlined" startIcon={<ArrowBackOutlinedIcon />} onClick={returnToSupplierDatabase}>Back</Button>
        </div>
        <div className={styles.uploadCard}>
          <div
            className={`${styles.fileUploadWrapper} ${isDragging ? styles.dragging : ""}`}
            onDragOver={(event) => { event.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={(event) => { event.preventDefault(); setIsDragging(false); selectFile(event.dataTransfer.files?.[0]); }}
          >
            <div className={styles.fileUpload}>
              <label className={styles.docText}>
                <span>{file ? file.name : "Drag and drop file here"}</span>
                <input ref={inputRef} type="file" accept={ACCEPTED_TYPES} onChange={(event) => selectFile(event.target.files?.[0])} />
                <p>(Supported format CSV/Excel)</p>
              </label>
              <button type="button" className={styles.browseButton} onClick={() => inputRef.current?.click()}>{file ? "Change" : "Browse"}</button>
            </div>
          </div>
          {file && <div className={styles.selectedFile}><InsertDriveFileOutlinedIcon /><span>{file.name}</span><button onClick={() => { setFile(null); setMappings([]); if (inputRef.current) inputRef.current.value = ""; }}>Remove</button></div>}
          {error && <p className={styles.error}>{error}</p>}
          {mappings.length > 0 && (
            <div className={styles.mappingPreview}>
              <div className={styles.mappingHeader}>
                <h2>Extracted supplier mappings</h2>
                <div className={styles.mappingHeaderActions}>
                  <span className={styles.mappingCount}>{importSummary?.count ?? mappings.length} total mappings</span>
                  <Button variant="contained" onClick={saveMappings} disabled={isSaving}>{isSaving ? "Saving..." : "Save Result"}</Button>
                </div>
              </div>
              <div className={styles.mappingTableScroll}>
                <table className={styles.mappingTable}>
                  <thead><tr><th>Item code</th><th>Item name</th><th>Vendor name</th><th>Supply location</th><th>Email</th><th>Email note</th></tr></thead>
                  <tbody>{mappings.map((mapping, index) => {
                    const location = mapping.supply_location || "-";
                    return <tr key={`${mapping.item_code}-${mapping.vendor_name}-${index}`}><td>{mapping.item_code || "-"}</td><td>{mapping.item_name || "-"}</td><td>{mapping.vendor_name || "-"}</td><td className={styles.locationCell}><span ref={(node) => { locationRefs.current[index] = node; }} className={expandedLocations[index] ? styles.locationExpanded : styles.locationText}>{location}</span>{locationOverflow[index] && <button type="button" className={styles.readMore} onClick={() => setExpandedLocations((current) => ({ ...current, [index]: !current[index] }))}>{expandedLocations[index] ? "Read less" : "Read more"}</button>}</td><td>{mapping.email || "-"}</td><td>{mapping.email_note || "-"}</td></tr>;
                  })}</tbody>
                </table>
              </div>
            </div>
          )}
          <div className={styles.uploadActions}>
            {mappings.length > 0 && <span className={styles.uploadStatus}>{mappings.length} mapping{mappings.length === 1 ? "" : "s"} extracted</span>}
            <Button variant="contained" onClick={uploadFile} disabled={!file || isUploading || mappings.length > 0}>{isUploading ? "Uploading..." : "Upload Documents"}</Button>
          </div>
        </div>
        <Modal open={saveSuccessOpen} onClose={returnToSupplierDatabase}>
          <Fade in={saveSuccessOpen}>
            <Box>
              <SuccessPopupDashboard popupImage={successPopupImg} popupHeroText="Supplier Database Saved!" popupDescription="Extracted supplier mappings have been saved successfully." popBtn={true} buyerBtn={true} buyerBtnOutline={true} btnText="Okay" btnClick={returnToSupplierDatabase} />
            </Box>
          </Fade>
        </Modal>
      </section>
    );
  }

  return (
    <section className={styles.page}>
      <div className={styles.header}>
        <div><h1>Supplier Database</h1><p>External suppliers and the items they provide for RFQ sourcing.</p></div>
        <Button variant="contained" onClick={() => { clearUploadData(); setIsUploadView(true); }}>Upload data</Button>
      </div>
      <div className={styles.tableCard}>
        <div className={styles.rfqListToolbar}>
          <SearchBar value={savedSearch} onChange={(query) => { setSavedSearch(query); setSavedPage(1); }} placeholder="Search item code, item name, supplier..." />
        </div>
        <DataTable headings={savedMappingsHeadings} tableData={savedMappings} tableSize="100%" buyer={true} stickyHead={true} isLoading={isSavedTableLoading} hideDefaultEmptyMessage={true} />
        {!isSavedTableLoading && <PaginationComponent pageNumber={savedPage} setPageNumber={(value) => value !== "" && setSavedPage(Number(value))} currentPage={savedPage} itemsPerPage={savedTotal} totalItems={Math.ceil(savedTotal / 10)} previouPageClick={() => setSavedPage((page) => Math.max(1, page - 1))} nextPageClick={() => setSavedPage((page) => Math.min(Math.ceil(savedTotal / 10), page + 1))} />}
      </div>
    </section>
  );
};

export default SupplierDatabase;
